import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton } from '@mui/material'
import { Add, Visibility } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import { listInventoryCounts, type InventoryCountListItem, type InventoryCountStatus } from '../../api/stocuri'
import CreateInventoryCountDialog from './CreateInventoryCountDialog'
import InventoryCountDetailDialog from './InventoryCountDetailDialog'

const STATUS_LABEL: Record<InventoryCountStatus, string> = { Draft: 'În lucru', Finalizat: 'Finalizat' }
const STATUS_COLOR: Record<InventoryCountStatus, 'default' | 'success'> = { Draft: 'default', Finalizat: 'success' }

export default function InventoryPage() {
  const [createOpen, setCreateOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['stocuri', 'inventar'], queryFn: listInventoryCounts })

  const columns = useMemo<GridColDef<InventoryCountListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 140, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'countDate', headerName: 'Data', width: 120 },
    { field: 'warehouseName', headerName: 'Depozit', flex: 1, minWidth: 180 },
    {
      field: 'status', headerName: 'Status', width: 140, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => <IconButton size="small" onClick={() => setDetailId(p.row.id)}><Visibility fontSize="small" /></IconButton>,
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Stocuri</Typography>
        <Typography variant="inherit" color="text.primary">Inventar</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Inventar periodic</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>Numărătoare nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <CreateInventoryCountDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => setDetailId(id)} />
      <InventoryCountDetailDialog id={detailId} onClose={() => setDetailId(null)} />
    </Stack>
  )
}
